import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetAssessmentsAccumulationMappings,
  useGetAssessmentsComparison,
} from '@/entities/assessments/api/generated/api';
import { useApiErrorHandler } from '@/shared/hooks/useApiErrorHandler';
import { DEFAULT_AMOUNT_UNIT } from '@/shared/lib/amount/convertAmount';
import { toApiSortParam } from '@/shared/lib/sort/toApiSortParam';
import { useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { Button, Skeleton, useSkeletonMinDisplay } from '@/shared/ui';
import { parseSortConditions } from '@/widgets/assessment/assessment-sort/helpers/sortProjects';
import { SideMenuHeader } from '@/widgets/right-side-menu/ui/SideMenuHeader';
import { AIAssessmentCard } from './AIAssessmentCard';
import { AssessmentComparisonAccordion } from './AssessmentComparisonAccordion';
import { AssessmentComparisonTagSection } from './AssessmentComparisonTagSection';
import { buildPlaceholderData, flattenAccumulations } from './findAccumulationFromComparison';
import { TargetHeader } from './TargetHeader';
/**
 * 歳出比較・査定の右サイドパネルコンテンツ
 *
 * 一覧データのキャッシュから即座にコンテンツを表示し、
 * 詳細APIの応答を待たずにユーザー体験を向上させる。
 * 詳細API応答後にmemo等の追加データで更新する。
 */
export const AssessmentComparisonSidePanel = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams({ from: '/_protected/assessment/$assessmentId/diff' });
  const search = useSearch({ from: '/_protected/assessment/$assessmentId/diff' });
  const accumulationId = search?.accumulationId;
  // 金額単位は URL のみで管理し永続化しない（親ページと同じ search を参照）。
  const amountUnit = search?.amount_unit ?? DEFAULT_AMOUNT_UNIT;
  const { t } = useTranslation();
  const requestClose = useRouteSidePanelStore((state) => state.requestClose);

  // 親ページ（useAssessmentComparisonPage）と同じ sort クエリで比較データを取得する。
  // sort 無しで取得すると前後移動が BE 既定順の隣へ移動し、表の表示順とずれるため
  // （URL の sort/order をパースして同一パラメータを渡す）。
  const sortParam = useMemo(
    () => toApiSortParam(parseSortConditions(search?.sort, search?.order)),
    [search?.sort, search?.order],
  );

  // 一覧ページと同じクエリキーを使うため、キャッシュがあれば即座にデータ取得。
  // getQueryData と異なりリアクティブなので、URLパラメータで直接アクセスした場合も
  // 一覧データ到着後に自動的にプレースホルダーが構築される。
  const { data: comparisonData } = useGetAssessmentsComparison(
    assessmentId,
    sortParam ? { sort: sortParam } : undefined,
  );

  // 階層走査は comparisonData が変わった時のみ再計算
  const allAccumulations = useMemo(
    () => (comparisonData ? flattenAccumulations(comparisonData.projects) : []),
    [comparisonData],
  );

  const placeholderData =
    comparisonData && accumulationId
      ? buildPlaceholderData({ allAccumulations, accumulationId, comparisonData })
      : null;

  const {
    data: accumulationMappingsData,
    isLoading: isAccumulationMappingsLoading,
    error: accumulationMappingsError,
  } = useGetAssessmentsAccumulationMappings(accumulationId ?? '', {
    query: { enabled: !!accumulationId },
  });

  const showSkeleton = useSkeletonMinDisplay(isAccumulationMappingsLoading && !placeholderData);

  useApiErrorHandler({
    error: accumulationMappingsError,
    navigate,
  });

  const displayData = accumulationMappingsData ?? placeholderData;

  if (!accumulationId || !displayData) {
    return null;
  }

  const alreadyExecutionAIAssessment: boolean = !!displayData.mappings.length;
  const currentIndex = allAccumulations.findIndex((item) => item.accumulationId === accumulationId);
  const previousAccumulationId =
    currentIndex > 0 ? allAccumulations[currentIndex - 1]?.accumulationId : undefined;
  const nextAccumulationId =
    currentIndex >= 0 && currentIndex < allAccumulations.length - 1
      ? allAccumulations[currentIndex + 1]?.accumulationId
      : undefined;

  return showSkeleton ? (
    <div className="space-y-4 p-4">
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  ) : (
    <div className="space-y-6">
      <SideMenuHeader
        onClose={() => {
          requestClose();
          navigate({ to: '.', search: (prev) => ({ ...prev, accumulationId: undefined }) });
        }}
        additionalMenuItems={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                navigate({
                  to: '.',
                  search: (prev) => ({ ...prev, accumulationId: previousAccumulationId }),
                });
              }}
              disabled={!previousAccumulationId}
              aria-label={t('assessment.messages.go-to-previous')}
            >
              <ArrowUpIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                navigate({
                  to: '.',
                  search: (prev) => ({ ...prev, accumulationId: nextAccumulationId }),
                });
              }}
              disabled={!nextAccumulationId}
              aria-label={t('assessment.messages.go-to-next')}
            >
              <ArrowDownIcon className="h-4 w-4" />
            </Button>
          </>
        }
      />

      <div className="space-y-6 px-4">
        <TargetHeader accumulationMappingsData={displayData} />
        <AssessmentComparisonTagSection
          assessmentsAccumulationId={displayData.assessments_accumulation_id}
          initialAccumulationTags={displayData.accumulation_tags}
          isLoading={!accumulationMappingsData}
        />
        <AIAssessmentCard
          alreadyExecutionAIAssessment={alreadyExecutionAIAssessment}
          comment={displayData.comment}
        />
        <AssessmentComparisonAccordion
          accumulationMappingsData={displayData}
          amountUnit={amountUnit}
          isMemoLoading={!accumulationMappingsData}
        />
      </div>
    </div>
  );
};
